export class SiteScrollReveal {
  private reveal?: IntersectionObserver;
  private counters?: IntersectionObserver;
  private timers: number[] = [];

  constructor(private root: ParentNode, private host: HTMLElement) {}

  refresh() {
    this.disconnect();
    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce || !('IntersectionObserver' in window)) { return; }
    this.host.classList.add('js-anim');

    const vh = window.innerHeight;
    const mark = (el: Element | null, type?: string | null, delay?: number) => {
      if (!el || el.classList.contains('rv')) { return; }
      if (el.getBoundingClientRect().top < vh * 0.92) { return; }
      el.classList.add('rv');
      if (type) { el.classList.add('rv-' + type); }
      if (delay) { (el as HTMLElement).style.setProperty('--rv-delay', delay + 'ms'); }
    };
    const each = (sel: string, fn: (el: HTMLElement) => void) => {
      this.root.querySelectorAll<HTMLElement>(sel).forEach(fn);
    };

    each('.section-head, .usp-spotlight-head, .scenario-intro-panel, .specialty-feature-head, .services-report-intro, .care-journey-intro, .hiw-section-head, .specialty-feature-footer, .placeholder-note, .compliance-row, .services-closing',
      el => mark(el));

    const grids = '.usp-spotlight-grid, .scenario-matrix, .steps-grid, .specialty-feature-grid, .promise-grid, .stat-row, .doctor-grid, .accordion, .icon-grid, .split, .care-journey, .compare-grid, .journey-strip, .submit-chip-row, .contact-info-list, .doc-card-grid, .next-steps-list, .report-meta, .reach-grid';
    each(grids, grid => {
      const kids = Array.prototype.filter.call(grid.children, (c: Element) => !c.classList.contains('journey-arrow')) as Element[];
      const cols = Math.max(1, Math.round(grid.getBoundingClientRect().width / Math.max(1, (kids[0] || grid).getBoundingClientRect().width)));
      kids.forEach((c, i) => mark(c, null, Math.min((i % Math.max(cols, 1)) * 90 + Math.floor(i / Math.max(cols, 1)) * 40, 520)));
    });

    each('.step-detail', row => {
      const imgRight = row.classList.contains('step-detail-image-right');
      mark(row.querySelector('.step-detail-visual'), imgRight ? 'right' : 'left');
      mark(row.querySelector('.step-detail-content'), imgRight ? 'left' : 'right', 120);
    });
    each('.submit-copy', el => mark(el, 'left'));
    each('.submit-visual', el => mark(el, 'right', 120));
    each('.report-showcase > *, .report-doc-section, .report-section, .report-cover, .profile-aside, .profile-main, .story', el => mark(el));
    each('.cta-band .cta-inner, .stories-split .story', el => mark(el, 'zoom'));

    this.reveal = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) { return; }
        const el = entry.target as HTMLElement;
        this.reveal.unobserve(el);
        el.classList.add('in-view');
        const delay = parseInt(el.style.getPropertyValue('--rv-delay'), 10) || 0;
        this.timers.push(window.setTimeout(() => {
          el.classList.remove('rv', 'rv-left', 'rv-right', 'rv-zoom', 'in-view');
          el.style.removeProperty('--rv-delay');
        }, 900 + delay));
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -6% 0px' });
    each('.rv', el => this.reveal.observe(el));

    this.counters = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) { return; }
        const el = entry.target as HTMLElement;
        const original = el.textContent || '';
        this.counters.unobserve(el);
        const m = original.match(/^(\D*)([\d,]+)(.*)$/);
        if (!m) { return; }
        const target = parseInt(m[2].replace(/,/g, ''), 10);
        const useComma = m[2].indexOf(',') > -1;
        let start: number | null = null;
        const dur = 1400;
        const fmt = (n: number) => useComma ? n.toLocaleString('en-US') : String(n);
        const step = (ts: number) => {
          if (start === null) { start = ts; }
          const p = Math.min((ts - start) / dur, 1);
          const eased = 1 - Math.pow(1 - p, 3);
          el.textContent = m[1] + fmt(Math.round(target * eased)) + m[3];
          if (p < 1) { requestAnimationFrame(step); } else { el.textContent = original; }
        };
        requestAnimationFrame(step);
      });
    }, { threshold: 0.6 });
    each('.stat b', el => this.counters.observe(el));
  }

  disconnect() {
    if (this.reveal) { this.reveal.disconnect(); }
    if (this.counters) { this.counters.disconnect(); }
    this.timers.forEach(t => clearTimeout(t));
    this.timers = [];
  }
}
